import api from './api';

const POPUP_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Returns the origin (scheme + host + port) of the backend API.
 * Used to validate postMessage events sent by the OAuth callback page.
 */
function getBackendOrigin() {
  try {
    return new URL(process.env.REACT_APP_API_LOCAL_URL).origin;
  } catch {
    return window.location.origin;
  }
}

/**
 * Returns the backend base URL without a trailing slash.
 */
function getBackendBase() {
  return (process.env.REACT_APP_API_LOCAL_URL || '').replace(/\/$/, '');
}

class ContaAzulOAuthService {
  /**
   * Executes the full backend-centralised ContaAzul OAuth flow:
   *
   * 1. Calls POST /oauth/conta-azul/launch (authenticated) to get a
   *    short-lived launch token.
   * 2. Opens a popup pointing to GET /oauth/conta-azul/start?t={token}
   *    on the backend. The backend redirects the popup to ContaAzul.
   * 3. ContaAzul redirects back to the backend's fixed redirect_uri
   *    (/contaazul/oauth/exchange), which exchanges the code, marks the
   *    sync as configured and posts a message to this window.
   * 4. Resolves with true on success; rejects with an Error on failure.
   *
   * No access token is ever forwarded to the frontend.
   */
  static async completeAuth(syncId) {
    // Open popup synchronously BEFORE any async call so it stays within the
    // user-gesture context. Browsers block window.open() called after an await.
    const top = Math.floor((window.screen.height / 2) - 300);
    const left = Math.floor((window.screen.width / 2) - 300);
    const popupName = `ContaAzulOAuth_${syncId}_${Date.now()}`;

    const popup = window.open(
      'about:blank',
      popupName,
      `width=600,height=700,popup=1,top=${top},left=${left}`,
    );

    if (!popup) {
      throw new Error(
        'Não foi possível abrir o popup de autorização. ' +
        'Verifique se popups estão bloqueados no seu navegador.',
      );
    }

    // Step 1: obtain short-lived launch token (requires valid JWT)
    let launchToken;
    try {
      const launchResponse = await api.post('/oauth/conta-azul/launch', { syncId });
      if (launchResponse.status !== 200) {
        popup.close();
        throw new Error(`Erro ao obter token de launch: status ${launchResponse.status}`);
      }
      launchToken = launchResponse.data?.launchToken;
      if (!launchToken) {
        popup.close();
        throw new Error('Launch token não recebido do servidor.');
      }
    } catch (err) {
      try { popup.close(); } catch (_) { /* ignore */ }
      throw err;
    }

    const backendBase = getBackendBase();
    const backendOrigin = getBackendOrigin();
    const popupUrl = `${backendBase}/oauth/conta-azul/start?t=${encodeURIComponent(launchToken)}`;

    // Step 2: navigate the already-open popup to the backend OAuth start URL
    popup.location.href = popupUrl;

    // Step 3: wait for postMessage from backend callback page
    return new Promise((resolve, reject) => {
      let settled = false;

      function cleanup() {
        clearInterval(closedInterval);
        clearTimeout(timeoutHandle);
        window.removeEventListener('message', onMessage);
        settled = true;
      }

      function onMessage(event) {
        // Only accept messages from the backend origin
        if (event.origin !== backendOrigin) return;

        const { type, success, error } = event.data || {};
        if (type !== 'contaazul-oauth') return;

        cleanup();
        if (success) {
          resolve(true);
        } else {
          reject(new Error(error || 'Autenticação com Conta Azul falhou.'));
        }
      }

      // Detect popup closure. When the backend returns error HTML it calls
      // window.close() immediately after postMessage — so popup.closed can
      // become true before the message event is dispatched. We keep the
      // listener alive for 500 ms after closure to let the postMessage arrive.
      const closedInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(closedInterval);
          clearTimeout(timeoutHandle);
          setTimeout(() => {
            if (!settled) {
              window.removeEventListener('message', onMessage);
              settled = true;
              reject(new Error('Popup foi fechado pelo usuário antes de concluir a autorização.'));
            }
          }, 500);
        }
      }, 500);

      // Safety timeout
      const timeoutHandle = setTimeout(() => {
        cleanup();
        try { popup.close(); } catch (_) { /* ignore */ }
        reject(new Error('Timeout: a autorização OAuth2 não foi concluída em 5 minutos.'));
      }, POPUP_TIMEOUT_MS);

      window.addEventListener('message', onMessage);
    });
  }
}

export default ContaAzulOAuthService;
